import { useState } from 'react';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { useMutation } from '@tanstack/react-query';
import { Post } from 'lib/types';
import { ESPost } from 'lib/types/posts';
import { Credit } from 'lib/types/credits';
import { Tag } from 'lib/types/tags';
import { userIsProOrAdminOrOG, userIsAdmin, userHasAccessToHeroMode } from 'lib/helpers';
import { RolesProvider } from 'lib/contexts/rolesContext';
import { useUpgradeModal } from 'components/UpgradeModal';
import { useApplicationState } from 'lib/contexts/ApplicationState';
import { useTeams } from 'lib/contexts/teamsContext';
import {
  Add, Info, Tag as TagIcon, FilterNone, FolderOpen,
} from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { MobileTooltip } from 'components/MobileTooltip';
import { UsernameModal } from 'components/UsernameModal';
import { useModal } from 'mui-modal-provider';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { BulkSavePostsToProjectsModal } from 'components/SaveToList/BulkSavePostsToProjectsModal';
import { DrawerType, Method } from './AddDrawer';
import { FilterPostResult } from './FilterPostResult';

interface Props {
  posts: Post[];
  isLoading: boolean;
  error?: Error;
  selectedPostIds: number[];
  setSelectedPostIds: (ids: number[]) => void;
  drawer?: DrawerType;
  method?: Method;
  setDrawer: (drawer: DrawerType | undefined) => void;
  setSimilarPosts: (posts: Post[]) => void;
  setToCopyTags: (tags: Tag[]) => void;
  setToCopyCredits: (credits: Credit[]) => void;
  isProfileOwner?: boolean;
}

export const Manager = ({
  posts, isLoading, error, selectedPostIds, setSelectedPostIds, drawer, method,
  setDrawer, setSimilarPosts, setToCopyTags, setToCopyCredits, isProfileOwner,
}: Props) => {
  const router = useRouter();
  const { currentUser, api, isAuthed } = useApplicationState();
  const { showModal } = useModal();
  const teams = useTeams();

  const [showDialog, setShowDialog] = useState(false);
  const [usernameInfo, setUsernameInfo] = useState<string | undefined>();

  const [selectedMatchId, setSelectedMatchId] = useState<string | undefined>();

  const { showUpgradeModal } = useUpgradeModal();

  // Determine if a specific post's checkbox should be disabled
  const isPostSelectionDisabled = (post: Post): boolean => {
    // Logged-out users cannot select any posts
    if (!currentUser) {
      return true;
    }

    // Admins can select any post
    if (userIsAdmin(currentUser)) {
      return false;
    }

    // Hero mode users can select any post
    if (userHasAccessToHeroMode(currentUser, teams)) {
      return false;
    }

    // If viewing imported posts (router.query.ownership === 'imported'), allow selection
    if (router.query.ownership === 'imported') {
      return false;
    }

    // Profile owners can select their own posts (for Match Posts feature)
    if (isProfileOwner) {
      return false;
    }

    // Non-PRO users cannot select any posts
    if (!userIsProOrAdminOrOG(currentUser)) {
      return true;
    }

    // PRO users can select posts they own, manage, or imported
    const esPost = post as ESPost;
    const userId = currentUser.id;

    // Check if the post has any of the ownership fields
    const ownedByUserIds = esPost.ownedByUserIds || [];
    const managedByUserIds = esPost.managedByUserIds || [];
    const importedByUserIds = esPost.importedByUserIds || [];

    const canAccess = ownedByUserIds.includes(userId)
      || managedByUserIds.includes(userId)
      || importedByUserIds.includes(userId);

    return !canAccess;
  };

  const disabled = !currentUser || !selectedPostIds || isEmpty(selectedPostIds);

  // Fetch similar posts mutation
  const {
    mutateAsync: fetchSimilarPosts,
  } = useMutation<Post[], Error, number>({
    mutationFn: async (postId: number) => api.getSimilarPosts(postId),
    onSuccess: (data) => {
      setSimilarPosts(data.filter((_p: Post) => !selectedPostIds.find((m) => m === _p.id)));
    },
  });

  const showSimilarPosts = async () => {
    setSimilarPosts([]);
    const postCount = selectedPostIds.length;
    if (postCount > 0) {
      const postId = selectedPostIds[0];
      await fetchSimilarPosts(postId);
    }
  };

  // Merge matches mutation
  const {
    mutateAsync: mergeMatchesMutation,
  } = useMutation<void, Error, { matchId: string; matchIds: string[] }>({
    mutationFn: async ({ matchId, matchIds }): Promise<void> => {
      await api.mergeMatches(matchId, matchIds);
    },
  });

  const openOrUpgrade = (feature: string, open: () => void) => {
    if (!currentUser?.username) {
      setShowDialog(true);
      setUsernameInfo(`Please add a username to ${feature}.`);
      return;
    }

    if (userIsProOrAdminOrOG(currentUser)) {
      open();
    } else {
      showUpgradeModal(`Upgrade to ${feature}`);
    }
  };

  const giveCredits = () => {
    setDrawer('credit');
  };

  const copy = (post: Post) => {
    openOrUpgrade('copy from a post', () => {
      setToCopyCredits(post.credits || []);
      setToCopyTags(post.tags || []);
      window.scrollTo({ top: 0 });
    });
  };

  const selectMatch = async (matchId: string) => {
    const alreadySelected = selectedMatchId === matchId;
    if (!alreadySelected && selectedMatchId) {
      // if a match is already selected, ask if user wants to merge
      const modal = showModal(ConfirmationModal, {
        text: 'You have selected two existing separate matches.  Would you like to merge them?  This action cannot be undone.',
        primaryAction: 'Y',
        onYes: async () => {
          await mergeMatchesMutation({ matchId: selectedMatchId, matchIds: [matchId] });
          modal.destroy();
        },
        onNo: () => {
          modal.destroy();
        },
      });
      return;
    }

    if (!alreadySelected) {
      setSelectedMatchId(matchId);
    } else {
      setSelectedMatchId(undefined);
    }
  };

  const handleSaveToProject = () => {
    const modal = showModal(BulkSavePostsToProjectsModal, {
      postIds: selectedPostIds,
      onClose: () => {
        modal.destroy();
      },
    });
  };

  const renderActionButtonsSkeleton = () => (
    <div className="flex items-center mr-2 my-2">
      <Skeleton variant="rectangular" width={120} height={32} className="mr-2 rounded" />
      <Skeleton variant="rectangular" width={100} height={32} className="mr-2 rounded" />
      <Skeleton variant="rectangular" width={90} height={32} className="mr-2 rounded" />
      <Skeleton variant="rectangular" width={110} height={32} className="mr-2 rounded" />
      <Skeleton variant="rectangular" width={120} height={32} className="mr-2 rounded" />
    </div>
  );

  const renderActionButtons = () => (
    <div className="flex items-center mr-2 my-2">
      {(!currentUser || !userIsProOrAdminOrOG(currentUser)) && (
        <div className="relative mr-2">
          <button
            type="button"
            onClick={() => showUpgradeModal('Subscribe to PRO to bulk credit and tag posts!')}
            className="btn-secondary-xs md:whitespace-nowrap uppercase flex items-center bg-alpineTeal text-white py-2 px-2"
          >
            Subscribe to PRO for bulk actions
          </button>
        </div>
      )}
      <div className="relative mr-2">
        {disabled && (
          <MobileTooltip
            arrow
            className="absolute -top-2 -right-2"
            title="To add credits, first select a post."
          >
            <Info />
          </MobileTooltip>
        )}
        <button
          type="button"
          disabled={disabled}
          onClick={() => openOrUpgrade('bulk assign credits', giveCredits)}
          className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
        >
          <Add fontSize="small" className="mr-1" />
          Add Credits
        </button>
      </div>
      <div className="relative mr-2">
        {disabled && (
          <MobileTooltip
            arrow
            className="absolute -top-2 -right-2"
            title="To add tags, first select a post."
          >
            <Info />
          </MobileTooltip>
        )}

        <button
          type="button"
          disabled={disabled}
          className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
          onClick={() => openOrUpgrade('bulk add tags', () => setDrawer('tag'))}
        >
          <TagIcon fontSize="small" className="mr-1" />
          Add Tags
        </button>
      </div>
      <div className="relative mr-2">
        {(disabled || selectedPostIds?.length < 2) && (
          <MobileTooltip
            arrow
            className="absolute -top-2 -right-2"
            title="To match posts, first select at least two posts."
          >
            <Info />
          </MobileTooltip>
        )}

        <button
          type="button"
          disabled={disabled || selectedPostIds?.length < 2}
          className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
          onClick={() => {
            setDrawer('match');
            showSimilarPosts();
          }}
        >
          <FilterNone fontSize="small" className="mr-1" />
          Match Posts
        </button>
      </div>
      <div className="relative mr-2">
        {disabled && (
          <MobileTooltip
            arrow
            className="absolute -top-2 -right-2"
            title="To add to projects, first select a post."
          >
            <Info />
          </MobileTooltip>
        )}

        <button
          type="button"
          onClick={handleSaveToProject}
          className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
          disabled={disabled}
        >
          <FolderOpen fontSize="small" className="mr-1" />
          Add to project
        </button>
      </div>
    </div>
  );

  const handleSelectionChange = (postIds: number[]) => {
    setSelectedPostIds(postIds);
  };

  return (
    <RolesProvider>
      <div className="flex flex-wrap sm:flex-nowrap justify-between items-center">
        {isAuthed ? renderActionButtons() : renderActionButtonsSkeleton()}
      </div>
      <div className="mt-2 w-full">
        <FilterPostResult
          posts={posts}
          selectedPostIds={selectedPostIds}
          isLoading={isLoading}
          error={error}
          onChange={handleSelectionChange}
          drawerOpen={drawer !== undefined}
          method={method}
          onCopy={copy}
          onSelectMatch={selectMatch}
          isPostSelectionDisabled={isPostSelectionDisabled}
        />
      </div>
      {showDialog
        && <UsernameModal saveLabel="SAVE" info={usernameInfo} onCancel={() => setShowDialog(false)} onSave={() => router.reload()} />}
    </RolesProvider>
  );
};
